import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import React from 'react';
import { computeProjectCostRows } from './payroll/projectCostFormula';

const fmtCur = (n) => new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TH = { fontSize: '0.75rem', fontWeight: 700, background: '#ffedd5', color: '#9a3412', padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #fed7aa', borderRight: '1px solid #fed7aa' };
const TD_TOTAL = { fontWeight: 700, background: '#ffedd5', color: '#9a3412', borderTop: '2px solid #f97316', borderRight: '1px solid #fdba74', padding: '6px 8px', textAlign: 'right' };
const TD_ROW = { padding: '5px 8px', borderBottom: '1px solid #fed7aa', borderRight: '1px solid #fed7aa', fontSize: '0.82rem' };
const IS = { padding: '2px 4px', fontSize: '0.78rem', textAlign: 'right', width: '100%', border: '1px solid #d1d5db', borderRadius: 4, outline: 'none', boxSizing: 'border-box' };

const DEFAULT_OT_MULT = 1.5;

// totalSsoAmount ไม่ได้ใช้ในไฟล์นี้แล้ว (total_sso คำนวณจาก employee_deductions ใน computeProjectCostRows เอง)
// ผู้เรียกที่ยังส่ง prop นี้มา (เช่น HrReport.js) ไม่ต้องแก้ — extra prop ที่ไม่ถูก destructure ไว้ก็แค่เฉยๆ
// existingLaborProjectIds/onCreateMissingExpense/onCreateAllMissingExpenses: ใส่มาเฉพาะตอนเรียกจาก
// HrReportV2.js เท่านั้น (ดูปุ่ม "สร้างรายการ" ด้านล่าง) ผู้เรียกอื่น (HrSalaryDetail.js, HrReport.js) ไม่ส่งมา
// คอลัมน์/ปุ่มพวกนี้เลยไม่โผล่ให้เห็น
export default function TimestampProjectCost({
  timestamp, filteredEmployeeIds, existingLaborProjectIds, onCreateMissingExpense, onCreateAllMissingExpenses,
}) {
  const ovKey = (projId, empId) => `${projId}__${empId}`;
  const [overrides, setOverrides] = React.useState({});
  const [globalOtMult, setGlobalOtMult] = React.useState(DEFAULT_OT_MULT);
  const [, setExpandedProjects] = React.useState(new Set());
  const [creatingProjectId, setCreatingProjectId] = React.useState(null);
  const [creatingAll, setCreatingAll] = React.useState(false);

  const handleCreateClick = async (row) => {
    if (!onCreateMissingExpense) return;
    setCreatingProjectId(row.project_id);
    try {
      await onCreateMissingExpense(row);
    } finally {
      setCreatingProjectId(null);
    }
  };

  const handleCreateAllClick = async (missingRows) => {
    if (!onCreateAllMissingExpenses || missingRows.length === 0) return;
    setCreatingAll(true);
    try {
      await onCreateAllMissingExpenses(missingRows);
    } finally {
      setCreatingAll(false);
    }
  };

  const computeEmp = React.useCallback(
    (emp, projId) => {
      const key = ovKey(projId, emp.employee_id);
      const ov = overrides[key] || {};
      const origHours = Math.round((emp.rate || 0) * 8 * 100) / 100;
      const origRate = Math.round(((emp.salary_per_day || 0) / 8) * 100) / 100;
      const origOtHours = origRate > 0 ? Math.round(((emp.ot_amount || 0) / (origRate * DEFAULT_OT_MULT)) * 100) / 100 : 0;
      const hours = ov.hours !== undefined ? parseFloat(ov.hours) || 0 : origHours;
      const ratePerHour = ov.ratePerHour !== undefined ? parseFloat(ov.ratePerHour) || 0 : origRate;
      const otMult = ov.otMult !== undefined ? Math.max(1, parseFloat(ov.otMult) || 1) : globalOtMult;
      // ไม่มี override ให้ใช้ยอดที่คำนวณมาจาก MergedPayrollTableV2 ตรงๆ — ห้ามแตกเป็น hours/rate แล้วคูณกลับ
      // เพราะการปัดเศษแยกสองตัว (origHours, origRate) แล้วคูณ+ปัดอีกรอบ ทำให้ยอดคลาดจากต้นฉบับทีละเศษสตางค์
      // พอมีพนักงาน/checkin เยอะ ความคลาดนี้จะสะสมจนยอดรวมสองตารางไม่ตรงกัน (ห่างได้หลักบาท)
      const hasHoursRateOverride = ov.hours !== undefined || ov.ratePerHour !== undefined;
      // OT ขึ้นกับ ratePerHour ด้วย (ไม่ใช่แค่ otMult) — ถ้า rate ถูกแก้ ต้องคำนวณ OT ใหม่ด้วยเช่นกัน
      const hasOtRecompute = ov.ratePerHour !== undefined || otMult !== DEFAULT_OT_MULT;
      return {
        ...emp,
        hours,
        ratePerHour,
        otMult,
        origOtHours,
        inputHours: ov.hours !== undefined ? ov.hours : origHours,
        inputRate: ov.ratePerHour !== undefined ? ov.ratePerHour : origRate,
        inputOtMult: ov.otMult !== undefined ? ov.otMult : globalOtMult,
        amount: hasHoursRateOverride
          ? Math.round(hours * ratePerHour * 100) / 100
          : (emp.amount || 0),
        ot_amount: hasOtRecompute
          ? Math.round(origOtHours * ratePerHour * otMult * 100) / 100
          : (emp.ot_amount || 0),
      };
    },
    [overrides, globalOtMult],
  );

  const setOv = (projId, empId, field, value) => {
    const key = ovKey(projId, empId);
    setOverrides((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  };

  const toggleProject = (projId) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projId)) next.delete(projId);
      else next.add(projId);
      return next;
    });
  };

  const rawDashboard = React.useMemo(() => {
    const raw = timestamp?.dashboard || [];
    if (!filteredEmployeeIds || filteredEmployeeIds.size === 0) return raw;
    return raw.map((project) => {
      const emps = (project.employees || []).filter((e) => filteredEmployeeIds.has(e.employee_id?.toString()));
      if (emps.length === 0) return null;
      const total_salary = emps.reduce((s, e) => s + (e.amount || 0), 0);
      const total_ot = emps.reduce((s, e) => s + (e.ot_amount || 0), 0);
      const total_allowance = emps.reduce((s, e) => s + (e.allowance || 0), 0);
      const total_field_allowance = emps.reduce((s, e) => s + (e.field_allowance_amount || 0), 0);
      return { ...project, employees: emps, total_salary, total_ot, total_allowance, total_field_allowance, total_spent: total_salary + total_ot };
    }).filter(Boolean);
  }, [timestamp?.dashboard, filteredEmployeeIds]);

  const costResult = React.useMemo(() => {
    const base = rawDashboard.map((project) => {
      const projId = project.project_id || project.project_number;
      const employees = (project.employees || []).map((emp) => computeEmp(emp, projId));
      const total_salary = employees.reduce((s, e) => s + (e.amount || 0), 0);
      const total_ot = employees.reduce((s, e) => s + (e.ot_amount || 0), 0);
      const total_allowance = employees.reduce((s, e) => s + (e.allowance || 0), 0);
      const total_field_allowance = employees.reduce((s, e) => s + (e.field_allowance_amount || 0), 0);
      return { ...project, employees, total_salary, total_ot, total_allowance, total_field_allowance };
    });
    // คำนวณ "รวม" ด้วยสูตรกลาง (ใช้ร่วมกับ PDF export ด้วย กันสูตรเพี้ยนคนละที่) — total_sso คำนวณจาก
    // employee_deductions ของแต่ละแถวเอง (ไม่ใช่กระจาย totalSsoAmount แบบ top-down อีกต่อไป ดู projectCostFormula.js)
    // total_payable ("ที่ต้องจ่าย") มากับ base อยู่แล้ว (คำนวณที่ MergedPayrollTableV2.js — projectDashboard)
    return computeProjectCostRows(base);
  }, [rawDashboard, computeEmp]);

  const dashboard = costResult.rows;

  // โครงการที่ column "รายการเบิก" กำลังโชว์ปุ่ม "สร้างรายการ" อยู่จริง (เกณฑ์เดียวกับที่ใช้ตัดสินใจต่อแถว
  // ด้านล่างเป๊ะ) — ใช้กับปุ่ม "สร้างรายการเบิกที่ขาดทั้งหมด" บน toolbar กันเวลามีหลายโครงการต้องกดทีละอัน
  const missingLaborRows = React.useMemo(() => {
    if (!onCreateMissingExpense || !existingLaborProjectIds) return [];
    return dashboard.filter((row) => row?.project_id
      && (row?.total_payable || 0) > 0
      && !existingLaborProjectIds.has(String(row.project_id)));
  }, [dashboard, existingLaborProjectIds, onCreateMissingExpense]);

  const grandTotalSalary = React.useMemo(
    () => dashboard.reduce((s, r) => s + (r.total_salary || 0), 0),
    [dashboard],
  );

  const hasOverrides = Object.keys(overrides).length > 0 || globalOtMult !== DEFAULT_OT_MULT;
  const rec = !filteredEmployeeIds && !hasOverrides ? timestamp?.reconciliation : null;

  const renderReconciliation = () => {
    if (!rec) return null;
    const { balanced } = rec;
    const bgColor = balanced ? '#f0fdf4' : '#fff7ed';
    const borderColor = balanced ? '#16a34a' : '#f97316';
    const textColor = balanced ? '#15803d' : '#c2410c';
    const Icon = balanced ? CheckCircleIcon : WarningAmberIcon;
    const salaryTooltip = rec.salary_balanced
      ? `ค่าแรงตรง: ${fmtCur(rec.employee_total_salary)}`
      : `ค่าแรงพนักงาน: ${fmtCur(rec.employee_total_salary)} | ตามโปรเจค: ${fmtCur(rec.project_total_salary)} | ส่วนต่าง: ${fmtCur(rec.diff_salary)}`;
    const otTooltip = rec.ot_balanced
      ? `OT ตรง: ${fmtCur(rec.employee_total_ot)}`
      : `OT พนักงาน: ${fmtCur(rec.employee_total_ot)} | ตามโปรเจค: ${fmtCur(rec.project_total_ot)} | ส่วนต่าง: ${fmtCur(rec.diff_ot)}`;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 6, marginTop: 8, fontSize: '0.82rem', color: textColor, fontWeight: 500, flexWrap: 'wrap' }}>
        <Icon style={{ fontSize: 18, color: borderColor }} />
        <span>{balanced ? 'ยอดรวมตรงกัน' : 'ยอดรวมไม่ตรง'}</span>
        <span style={{ margin: '0 4px', opacity: 0.4 }}>|</span>
        <span title={salaryTooltip} style={{ cursor: 'default', textDecoration: rec.salary_balanced ? 'none' : 'underline dotted' }}>
          ค่าแรง {rec.salary_balanced ? '✓' : `ต่าง ${fmtCur(Math.abs(rec.diff_salary))} บาท`}
        </span>
        <span style={{ margin: '0 4px', opacity: 0.4 }}>|</span>
        <span title={otTooltip} style={{ cursor: 'default', textDecoration: rec.ot_balanced ? 'none' : 'underline dotted' }}>
          OT {rec.ot_balanced ? '✓' : `ต่าง ${fmtCur(Math.abs(rec.diff_ot))} บาท`}
        </span>
      </div>
    );
  };

  const renderEmployeeSubTable = (row) => {
    const projId = row.project_id || row.project_number;
    const emps = row.employees || [];
    if (emps.length === 0) return null;
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', background: '#fffbf5', borderTop: '1px dashed #fed7aa' }}>
        <thead>
          <tr>
            {['ชื่อพนักงาน', 'ชม.', '฿/ชม', 'ชม.OT', 'ตัวคูณ OT', 'ค่าแรง', 'OT'].map((h) => (
              <th key={h} style={{ ...TH, fontSize: '0.73rem', color: '#7c2d12', padding: '4px 6px' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {emps.map((emp, i) => (
            <tr key={emp.employee_id || i}>
              <td style={{ ...TD_ROW, padding: '4px 6px' }}>
                {emp.name || emp.employee_id}
                {emp.role && <span style={{ color: '#9ca3af', marginLeft: 4, fontSize: '0.7rem' }}>{`(${emp.role})`}</span>}
              </td>
              <td style={{ ...TD_ROW, padding: '4px 4px', width: 80 }}>
                <input type="number" value={emp.inputHours} min={0} step={0.5} style={{ ...IS, width: 72 }} onChange={(e) => setOv(projId, emp.employee_id, 'hours', e.target.value)} />
              </td>
              <td style={{ ...TD_ROW, padding: '4px 4px', width: 110 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input type="number" value={emp.inputRate} min={0} step={1} style={{ ...IS, width: 80 }} onChange={(e) => setOv(projId, emp.employee_id, 'ratePerHour', e.target.value)} />
                  <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>฿</span>
                </div>
              </td>
              <td style={{ ...TD_ROW, textAlign: 'center', color: '#6b7280', width: 60 }}>{emp.origOtHours > 0 ? emp.origOtHours : '—'}</td>
              <td style={{ ...TD_ROW, padding: '4px 4px', width: 80 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input type="number" value={emp.inputOtMult} min={1} max={5} step={0.25} style={{ ...IS, width: 56 }} onChange={(e) => setOv(projId, emp.employee_id, 'otMult', e.target.value)} />
                  <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>×</span>
                </div>
              </td>
              <td style={{ ...TD_ROW, textAlign: 'right', fontWeight: 500 }}>{fmtCur(emp.amount)}</td>
              <td style={{ ...TD_ROW, textAlign: 'right', fontWeight: 500 }}>{fmtCur(emp.ot_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  void toggleProject;
  void renderEmployeeSubTable;

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.12)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <div style={{ padding: 16 }}>
          {/* ตัวคูณ OT toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 500 }}>ตัวคูณ OT:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #d1d5db', borderRadius: 4, overflow: 'hidden', height: 32 }}>
              <input type="number" value={globalOtMult} min={1} max={5} step={0.25}
                style={{ width: 44, padding: '0 6px', border: 'none', outline: 'none', fontSize: '0.875rem', textAlign: 'right' }}
                onChange={(e) => setGlobalOtMult(Math.max(1, parseFloat(e.target.value) || 1))} />
              <span style={{ padding: '0 6px', color: '#6b7280', fontSize: '0.8rem', borderLeft: '1px solid #e5e7eb', height: '100%', display: 'flex', alignItems: 'center' }}>×</span>
            </div>
            {[1.0, 1.5, 2.0, 3.0].map((v) => (
              <button key={v} onClick={() => setGlobalOtMult(v)}
                style={{ padding: '2px 10px', borderRadius: 12, border: `1px solid ${globalOtMult === v ? '#f97316' : '#d1d5db'}`, background: globalOtMult === v ? '#fff7ed' : '#fff', color: globalOtMult === v ? '#f97316' : '#374151', cursor: 'pointer', fontSize: '0.8rem', fontWeight: globalOtMult === v ? 700 : 400 }}>
                ×{v}
              </button>
            ))}
            {hasOverrides && (
              <button title="รีเซ็ตค่าทั้งหมดกลับต้นฉบับ" onClick={() => { setOverrides({}); setGlobalOtMult(DEFAULT_OT_MULT); }}
                style={{ padding: '3px 6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                <RestartAltIcon style={{ fontSize: 18 }} />
              </button>
            )}
            {hasOverrides && <span style={{ fontSize: '0.72rem', color: '#f97316', fontWeight: 500 }}>* กำลังใช้ค่าที่ปรับแต่ง</span>}
            {onCreateAllMissingExpenses && missingLaborRows.length > 0 && (
              <button
                type="button"
                disabled={creatingAll}
                onClick={() => handleCreateAllClick(missingLaborRows)}
                style={{
                  marginLeft: 'auto',
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: '1px solid #f97316',
                  background: creatingAll ? '#fed7aa' : '#fff7ed',
                  color: '#c2410c',
                  cursor: creatingAll ? 'default' : 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {creatingAll ? 'กำลังสร้าง...' : `สร้างรายการเบิกที่ขาดทั้งหมด (${missingLaborRows.length})`}
              </button>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th colSpan={onCreateMissingExpense ? 13 : 12} style={{ background: '#f97316', color: '#fff', fontWeight: 600, padding: '10px 8px', textAlign: 'center', fontSize: '0.95rem' }}>
                    ค่าแรงพนักงาน (ไม่หักภาษีและประกันสังคม)
                  </th>
                </tr>
                <tr>
                  {['โครงการ', 'ค่าแรง', 'OT', 'ปกส.', 'หักสาย', 'หัก ณ ที่จ่าย และอื่นๆ', 'ค่าแรงหลังหัก ปกส', 'เบี้ยเลี้ยง', 'ค่าออกสนาม', 'เงินได้อื่นๆ', 'ที่ต้องจ่าย', 'ต้นทุน'].map((h) => (
                    <th key={h} style={TH}>{h}</th>
                  ))}
                  {onCreateMissingExpense && <th style={TH}>รายการเบิก</th>}
                </tr>
              </thead>
              <tbody>
                {dashboard?.length > 0 ? (
                  <>
                    {dashboard.map((row, index) => (
                      <tr key={row.id || index} onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                        <td style={TD_ROW}>{row?.project_number}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>
                          {fmtCur(row?.total_salary)}
                          {grandTotalSalary > 0 && (
                            <div style={{ fontSize: '0.68rem', color: '#c2410c', fontWeight: 500 }}>
                              {((row?.total_salary || 0) / grandTotalSalary * 100).toFixed(1)}%
                            </div>
                          )}
                        </td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_ot)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_sso)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_late)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_tax)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.amount)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_allowance)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_field_allowance)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_bonus)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right', fontWeight: 600, color: '#c2410c' }}>
                          {fmtCur(row?.total_payable)}
                        </td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_spent)}</td>
                        {onCreateMissingExpense && (
                          <td style={{ ...TD_ROW, textAlign: 'center' }}>
                            {!row?.project_id || (row?.total_payable || 0) <= 0 ? (
                              '—'
                            ) : existingLaborProjectIds?.has(String(row.project_id)) ? (
                              <span style={{ color: '#16a34a', fontSize: '0.75rem', fontWeight: 600 }}>สร้างแล้ว ✓</span>
                            ) : (
                              <button
                                type="button"
                                disabled={creatingProjectId === row.project_id}
                                onClick={() => handleCreateClick(row)}
                                style={{
                                  padding: '3px 10px',
                                  borderRadius: 4,
                                  border: '1px solid #f97316',
                                  background: creatingProjectId === row.project_id ? '#fed7aa' : '#fff7ed',
                                  color: '#c2410c',
                                  cursor: creatingProjectId === row.project_id ? 'default' : 'pointer',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {creatingProjectId === row.project_id ? 'กำลังสร้าง...' : 'สร้างรายการ'}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...TD_TOTAL, textAlign: 'center' }}>รวมทั้งหมด</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_salary || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_ot || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_sso || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_late || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_tax || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.amount || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_allowance || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_field_allowance || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_bonus || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_payable || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_spent || 0), 0))}</td>
                      {onCreateMissingExpense && <td style={TD_TOTAL}></td>}
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={onCreateMissingExpense ? 13 : 12} style={{ textAlign: 'center', padding: '40px 8px', color: '#9ca3af', fontSize: '0.95rem' }}>
                      ไม่มีข้อมูล
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {renderReconciliation()}
        </div>
      </div>
    </div>
  );
}
